import * as React from 'react'
import {MouseEventHandler, ReactElement, ReactNode} from 'react'
import styled from 'styled-components'
import {Colors, FontSize, FontWeights} from '../../../../../styledComponents/Constants'
import {Comment, CommentInfo} from '../../repository/NatGetDisasterComment'
import {Box} from '@mui/material'
import {
    CenterRowFlexContainer,
    FullWidthLeftColumnFlexContainer,
    FullWidthRowFlexContainer,
    LeftRowFlexContainer,
    LeftTopRowFlexContainer,
    RowFlexContainer
} from '../../../../../styledComponents/FlexComponents'
import {IconBox, UserIcon} from '../../../../../components/assocDirector/AssocDirectorCommon'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faAngleDown, faAngleUp, faReply, faTrash} from '@fortawesome/free-solid-svg-icons'
import {ASSOC_GROUP, AVAILABLE_OR_NOT, HAVE_OR_NOT, ROLE_TYPE, VIEW_DO_OR_NOT, YES_OR_NOT} from '../../../../../CodeMaster'
import {BlockSpacer} from '../../../../../styledComponents/ResponsiveConvenience'
import {convertToRoomNumberForResident, judgeAuthorityCheck} from '../../../../common/util/Natutil'
import {Moment} from 'moment/moment'
import {NatDisasterHelper} from '../../common/NatDisasterHelper'
import moment from 'moment'
import {PlainLinkStyledButton} from '../../../../../styledComponents/Button'
import NatDisasterCommentInputForm, {DisasterCommentFile} from './NatDisasterCommentInputForm'
import {IconProp} from '@fortawesome/fontawesome-svg-core'
import {NatPostDisasterCommentRequest} from '../../repository/NatPostDisasterComment'
import {NatPostDisasterCommentFileUploadRequest} from '../../repository/NatPostDisasterCommentFileUpload'
import {Nullable, Optional} from '../../../../../app/TypeHelpers'
import Highlighter from 'react-highlight-words'
import equal from 'fast-deep-equal'
import NatDisasterActionMenu from '../NatDisasterActionMenu'
import NatDisasterCommentError, {InputError} from './NatDisasterCommentError'
import {HKConstants} from '../../../../../HKConstants'

interface Props {
    pastFlag?: boolean,
    commentInfo: CommentInfo
    commentReadMap: Map<number, number>
    searchKeyword: string
    posterNamePrivateFlag: boolean
    currentDateTime: Moment
    onClickReplyButton: (
        commentRequest: NatPostDisasterCommentRequest,
        fileRequests: NatPostDisasterCommentFileUploadRequest[]
    ) => Promise<Nullable<InputError>>
    onClickEditButton: (comment: Comment) => void
    onClickDeleteButton: (comment: Comment) => void
    onClickPreviewButton: (comment: Comment, startIndex: number) => void
    onReadComment: (commentIds: number[]) => Promise<void>
    onClearFileUploadError: () => void
    fileUploadError: Nullable<InputError>
}

interface State {
    isOpen: boolean
    isReply: boolean
    comment: string
    posterNamePrivateFlag: boolean
    newCommentFileList: DisasterCommentFile[]
    inputError: Optional<InputError>
}

export default class NatResidentDisasterCommentItem extends React.PureComponent<Props, State> {
    isPostAuthority: boolean

    constructor(props: Readonly<Props>) {
        super(props)
        const involvementMainComment = this.props.commentInfo.isPosterSelf === YES_OR_NOT.YES.value
        const involvementReplyComment = this.props.commentInfo.reply
            .some(reply => reply.isPosterSelf === YES_OR_NOT.YES.value)

        this.state = {
            isOpen: involvementMainComment || involvementReplyComment || this.props.searchKeyword !== '',
            isReply: false,
            comment: '',
            posterNamePrivateFlag: this.props.posterNamePrivateFlag,
            newCommentFileList: [],
            inputError: undefined
        }
        this.isPostAuthority = judgeAuthorityCheck(HKConstants.CMN.AUTH.POST_COMMENT_DISASTER_INFORMATION_SHARING)
        this.handleClickRegisterReplyButton = this.handleClickRegisterReplyButton.bind(this)
    }

    componentDidUpdate(prevProps: Props): void {
        if (!equal(prevProps.posterNamePrivateFlag, this.props.posterNamePrivateFlag)) {
            this.setState({posterNamePrivateFlag: this.props.posterNamePrivateFlag})
        }
    }

    handleOpen = async () => {
        const readCommentIds = this.props.commentInfo.reply
            .filter(reply => reply.deleteDatetime === null)
            .map(reply => reply.commentId)
        await this.props.onReadComment(readCommentIds)
        this.setState({isOpen: true})
    }

    handleClose = (): void => {
        this.setState({isOpen: false, isReply: false})
    }

    handleReplay = (): void => {
        this.setState({isReply: true})
    }

    handleCommentCansel = (): void => {
        this.setState({isReply: false, comment: '', newCommentFileList: [], inputError: undefined})
    }

    async handleClickRegisterReplyButton(): Promise<void> {
        const {commentId} = this.props.commentInfo
        const {comment, posterNamePrivateFlag} = this.state

        const fileRequests: NatPostDisasterCommentFileUploadRequest[] =
            this.state.newCommentFileList.map(file => {
                const data: string[] = String(file.fileData).split(',')
                return {fileName: file.fileName, fileData: data[1]}
            })
        const commentRequest: NatPostDisasterCommentRequest = {
            replySourceId: commentId,
            comment: comment,
            posterNamePrivateFlag: posterNamePrivateFlag ?
                VIEW_DO_OR_NOT.HIDDEN.value : VIEW_DO_OR_NOT.DISPLAY.value,
            imageAttachedFlag: fileRequests.length === 0 ?
                VIEW_DO_OR_NOT.HIDDEN.value : VIEW_DO_OR_NOT.DISPLAY.value
        }

        const inputError = await this.props.onClickReplyButton(commentRequest, fileRequests)
        if (inputError && !inputError.fileNameList) {
            this.setState({inputError: inputError})
        } else {
            this.setState({isReply: false, isOpen: true, comment: '', newCommentFileList: []})
        }
    }

    handleChangeCommentInput = (
        comment: string,
        posterNamePrivateFlag: boolean,
        commentFileList: DisasterCommentFile[],
        deleteFileNumberList: number[],
        inputError?: InputError
    ): void => {
        this.setState({
            comment: comment,
            posterNamePrivateFlag: posterNamePrivateFlag,
            newCommentFileList: [...commentFileList],
            inputError: inputError
        })
    }

    handleDeleteClick = (comment: Comment): void => {
        this.props.onClickDeleteButton(comment)
    }

    handleEditClick = (comment: Comment): void => {
        this.props.onClickEditButton(comment)
    }

    handleMenuClick = (comment: Comment): void => {
        const {fileUploadError} = this.props
        if (fileUploadError && (fileUploadError.commentId === comment.commentId)) {
            this.props.onClearFileUploadError()
        }
    }

    handlePreviewClick = (comment: Comment, startIndex: number): void => {
        this.props.onClickPreviewButton(comment, startIndex)
    }

    renderHighlighter(text: string): ReactElement {
        if (this.props.searchKeyword) {
            return <Highlighter
                highlightClassName="YourHighlightClass"
                searchWords={[this.props.searchKeyword]}
                textToHighlight={text}
            />
        }
        return <>{text}</>
    }

    renderReplyLink(): ReactElement {
        const {pastFlag} = this.props
        const {reply} = this.props.commentInfo

        let linkNode: ReactNode
        if (reply.length > 0) {
            linkNode = this.state.isOpen ?
                this.renderLinkButtonFunction(this.handleClose, '返信を閉じる', faAngleUp) :
                this.renderLinkButtonFunction(this.handleOpen, `返信を見る（${reply.length}件）`, faAngleDown)
        } else {
            linkNode = (!pastFlag && this.isPostAuthority && !this.state.isReply) ?
                this.renderLinkButtonFunction(this.handleReplay, '返信する', faReply) : ''
        }
        return <>{linkNode}</>
    }

    renderLinkButtonFunction(onClick: MouseEventHandler, text: string, icon: IconProp): ReactElement {
        return <StyledLink onClick={onClick}>
            <LeftRowFlexContainer>
                <IconBox><FontAwesomeIcon icon={icon}/></IconBox>
                {text}
            </LeftRowFlexContainer>
        </StyledLink>
    }

    renderPoster(commentInfo: Comment): ReactElement {
        const {searchKeyword} = this.props
        const {
            posterNamePrivateFlag,
            posterName,
            posterRidgeName,
            posterRoomNumber,
            posterRoleId
        } = commentInfo

        const assocGroupValues = ASSOC_GROUP.values().map(assoc => assoc.value)
        const isResident = posterRoleId.startsWith(ROLE_TYPE.RESIDENT.value)
        const isAssoc = assocGroupValues.includes(posterRoleId)
        const roomInfo = `${posterRidgeName} ${convertToRoomNumberForResident(posterRoomNumber)}号室`.trimStart()

        if (isResident || isAssoc) {
            if (posterNamePrivateFlag === VIEW_DO_OR_NOT.DISPLAY.value) {
                return (
                    <>
                        <RoomNumberText>
                            {this.renderHighlighter(roomInfo)}
                        </RoomNumberText>
                        <PosterNameText highlight={searchKeyword === posterName}>
                            {posterName}
                        </PosterNameText>
                        {isAssoc && <PosterNameText>（管理組合役員）</PosterNameText>}
                    </>
                )
            } else {
                return (
                    <>
                        <PosterNameText>
                            {this.renderHighlighter(roomInfo)}
                        </PosterNameText>
                        {isAssoc && <PosterNameText>（管理組合役員）</PosterNameText>}
                    </>
                )
            }
        } else {
            return (
                <PosterNameText highlight={searchKeyword === posterName}>
                    {posterName}
                </PosterNameText>
            )
        }
    }

    renderCommentHeader(commentInfo: Comment): ReactElement {
        const {pastFlag, currentDateTime} = this.props
        const {
            commentId,
            postDatetime,
            deleterRoleId,
            isDeleterSelf,
            deleteAvailability,
            editAvailability,
            isEdited
        } = commentInfo

        let formatPosterDate = NatDisasterHelper.formatDoNotDisplayCurrentYear(currentDateTime, moment(postDatetime))
        if (isEdited === HAVE_OR_NOT.HAVE.value) {
            formatPosterDate = `${formatPosterDate} 編集済み`
        }

        if (deleterRoleId) {
            return (
                <CommentHeader>
                    <RowFlexContainer>
                        <IconWithCircleStyle>
                            <FontAwesomeIcon icon={faTrash}/>
                        </IconWithCircleStyle>
                        <Box ml={'5px'} flex={1}>
                            <Box component={'span'} fontSize={FontSize.Middle}>
                                {NatDisasterHelper.getDeleteMessage(deleterRoleId, isDeleterSelf)}
                            </Box>
                        </Box>
                    </RowFlexContainer>
                </CommentHeader>
            )
        }

        const isDeletable = this.isPostAuthority && deleteAvailability === AVAILABLE_OR_NOT.AVAILABLE.value
        const isEditable = this.isPostAuthority && editAvailability === AVAILABLE_OR_NOT.AVAILABLE.value
        return (
            <FullWidthRowFlexContainer>
                <CommentHeader>
                    <CardContainer>
                        <Box><UserIcon iconSize={'32px'}/></Box>
                        <PosterContainer>
                            <Box lineHeight={'18px'}>
                                {this.renderPoster(commentInfo)}
                            </Box>
                            <CommentDatetime>
                                {formatPosterDate}
                            </CommentDatetime>
                        </PosterContainer>
                        <div>
                            {!pastFlag &&
                                <NatDisasterActionMenu
                                    id={`commentMenuButton-${commentId}`}
                                    label={'コメント'}
                                    isDeletable={isDeletable}
                                    isEditable={isEditable}
                                    onDelete={() => this.handleDeleteClick(commentInfo)}
                                    onEdit={() => this.handleEditClick(commentInfo)}
                                    onMenu={() => this.handleMenuClick(commentInfo)}
                                />
                            }
                        </div>
                    </CardContainer>
                </CommentHeader>
            </FullWidthRowFlexContainer>
        )
    }

    renderComment(commentInfo: Comment): ReactElement {
        const {pastFlag, commentReadMap, fileUploadError} = this.props
        const {commentId, comment, commentToAttachment} = commentInfo
        const readCount = commentReadMap.get(commentId) ?? 0

        if (!comment) {
            return <BlockSpacer height={'20px'}/>
        }

        return (
            <FullWidthLeftColumnFlexContainer>
                <BlockSpacer height={'5px'}/>
                {fileUploadError && fileUploadError.commentId === commentId &&
                    <>
                        <NatDisasterCommentError inputError={fileUploadError}/>
                        <Box height={'5px'}/>
                    </>
                }
                <CommentContainer>{this.renderHighlighter(comment)}</CommentContainer>
                {commentToAttachment.length > 0 &&
                    <ThumbnailImageContainer>
                        {commentToAttachment.map((file, index) => {
                            return (
                                <ThumbnailItem key={index} onClick={() => this.handlePreviewClick(commentInfo, index)}>
                                    <ThumbnailImage src={file.thumbnailPath}/>
                                </ThumbnailItem>
                            )
                        })}
                    </ThumbnailImageContainer>
                }
                <BlockSpacer height={'5px'}/>
                {!pastFlag &&
                    <Box textAlign={'right'} marginTop={'auto'} width={'100%'}>
                        既読：{readCount}
                    </Box>
                }
                <BlockSpacer height={'5px'}/>
            </FullWidthLeftColumnFlexContainer>
        )
    }

    render(): ReactElement {
        const {pastFlag, commentInfo} = this.props
        const {isOpen, isReply} = this.state
        const {reply} = this.props.commentInfo
        const isPost = !pastFlag && this.isPostAuthority

        return (
            <CommentArea>
                {this.renderCommentHeader(commentInfo)}
                <Box ml={'37px'}>
                    {this.renderComment(commentInfo)}
                </Box>
                {this.renderReplyLink()}
                {isOpen && reply.length > 0 &&
                    <Box ml={'34px'}>
                        {reply.map((replyCommentInfo, index) => {
                            const showVerticalDivider = isPost || reply.length !== index + 1
                            return (
                                <React.Fragment key={replyCommentInfo.commentId}>
                                    {index === 0 && <BlockSpacer height={'20px'}/>}
                                    <StretchRowFlexContainer>
                                        {this.renderCommentHeader(replyCommentInfo)}
                                    </StretchRowFlexContainer>
                                    <StretchRowFlexContainer>
                                        <VerticalDividerContainer>
                                            {showVerticalDivider && <VerticalDivider/>}
                                        </VerticalDividerContainer>
                                        <Box ml={'5px'}/>
                                        {this.renderComment(replyCommentInfo)}
                                        <BlockSpacer height={'5px'}/>
                                    </StretchRowFlexContainer>
                                </React.Fragment>
                            )
                        })}
                        {(isPost && !isReply) &&
                            <>
                                <BlockSpacer height={'5px'}/>
                                {this.renderLinkButtonFunction(this.handleReplay, '返信する', faReply)}
                            </>
                        }
                    </Box>
                }
                {(isPost && isReply) && <>
                    <BlockSpacer height={'5px'}/>
                    <Box ml={isOpen && reply.length > 0 ? '34px' : '0'}>
                        <NatDisasterCommentInputForm
                            id={`reply-${commentInfo.commentId}`}
                            submitButtonLabel={`返信する`}
                            clearButtonLabel={'キャンセル'}
                            onClickSubmitButton={this.handleClickRegisterReplyButton}
                            onClickCancelButton={this.handleCommentCansel}
                            onChange={this.handleChangeCommentInput}
                            comment={this.state.comment}
                            posterNamePrivateFlag={this.state.posterNamePrivateFlag}
                            placeholder={'返信を入力...'}
                            commentFileList={this.state.newCommentFileList}
                            deleteFileNumberList={[]}
                            inputError={this.state.inputError}
                        />
                    </Box>
                </>
                }
            </CommentArea>
        )
    }
}

const CommentArea = styled.div`
    padding: 10px 15px;
    border-bottom: 1px solid ${Colors.AccentLight};
    color: ${Colors.Body};
`

const StyledLink = styled(PlainLinkStyledButton)`
    position: relative;
    text-align: right;
    font-size: ${FontSize.Body};
`

const CommentHeader = styled(LeftTopRowFlexContainer)`
    width: 100%;
    min-height: 39px;
    align-items: center;
`

const CardContainer = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
`

const PosterContainer = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    padding-left: 5px;
`

const PosterNameText = styled.span<{ highlight?: boolean }>`
    font-size: ${FontSize.Middle};
    font-weight: ${FontWeights.Bold};
    line-height: 21px;
    ${props => props.highlight && 'background-color: #ffff00;'};
`

const RoomNumberText = styled.span`
    white-space: pre-wrap;
    padding-right: 5px;
    font-size: ${FontSize.Middle};
    font-weight: ${FontWeights.Bold};
    line-height: 21px;
`

const CommentDatetime = styled.div`
    font-size: ${FontSize.Small};
    color: ${Colors.AccentDark};
`

const StretchRowFlexContainer = styled(LeftRowFlexContainer)`
    align-items: stretch;
`

const VerticalDividerContainer = styled(CenterRowFlexContainer)`
    align-items: stretch;
    min-width: 32px;
`

const VerticalDivider = styled.hr`
    margin: 0;
    width: 4px;
    height: 100%;
    border: none;
    background-color: ${Colors.Background};
`

const IconWithCircleStyle = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 32px;
    height: 32px;
    font-size: 18px;
    color: #BDBDBD;

    background: #FFFFFF;
    border: 1px solid #D9D9D9;
    box-sizing: border-box;
    border-radius: 50%;
`

const CommentContainer = styled.div`
    white-space: pre-wrap;
    overflow-wrap: anywhere;
`

const ThumbnailImageContainer = styled.div`
    display: flex;
    gap: 5px;
    padding-top: 5px;
    flex-wrap: wrap;
`

const ThumbnailItem = styled.div`
    cursor: pointer;
`

const ThumbnailImage = styled.img`
    display: block;
    width: 134px;
    height: 134px;
    background-color: ${Colors.AccentLight};
    object-fit: contain;
`

